import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Grid,
  Pagination,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { HiOutlineBriefcase } from "react-icons/hi2";
import { FiChevronRight } from "react-icons/fi";
import CustomSearch from "../../Components/Common/CustomSearch";
import Loader from "../../Components/Common/Loader";
import InquiryDetailsDrawer from "../../Components/InvestorInquiries/InquiryDetailsDrawer";
import { GET_INVESTOR_INQUIRIES } from "../../Utils/Queries";

const ROWS_OPTIONS = [10, 25, 50];

function filterInquiries(items, searchRaw) {
  const term = searchRaw.trim().toLowerCase();
  if (!term) return items ?? [];
  return (items ?? []).filter(
    (item) =>
      item?.fullName?.toLowerCase().includes(term) ||
      item?.email?.toLowerCase().includes(term) ||
      item?.phoneNumber?.toLowerCase().includes(term) ||
      item?.organization?.toLowerCase().includes(term) ||
      item?.investmentRange?.toLowerCase().includes(term) ||
      item?._id?.toLowerCase().includes(term),
  );
}

function toPaginationFloat(value, fallback) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return n;
}

const InvestorInquiries = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const pageNum = useMemo(() => toPaginationFloat(page, 1), [page]);
  const limitNum = useMemo(
    () => toPaginationFloat(rowsPerPage, 10),
    [rowsPerPage],
  );

  const { data, loading } = useQuery(GET_INVESTOR_INQUIRIES, {
    variables: { page: pageNum, limit: limitNum },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const pagination = data?.fetchInvestorInquiries?.pagination ?? {};
  const totalItems = Number(pagination.totalItems ?? 0);
  const totalPages = Math.max(1, Number(pagination.totalPages ?? 1));

  const displayedItems = useMemo(() => {
    const list = data?.fetchInvestorInquiries?.items ?? [];
    return filterInquiries(list, search);
  }, [data, search]);

  const openDetails = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDrawerOpen(true);
  };

  const closeDetails = () => {
    setDrawerOpen(false);
  };

  const handlePageChange = (_, newPage) => {
    if (
      typeof newPage === "number" &&
      Number.isFinite(newPage) &&
      newPage >= 1
    ) {
      setPage(newPage);
    }
  };

  const start = totalItems === 0 ? 0 : (pageNum - 1) * limitNum + 1;
  const end = Math.min(pageNum * limitNum, totalItems);

  const hasData = Boolean(data?.fetchInvestorInquiries);

  return (
    <>
      {loading && !hasData && <Loader />}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "16px",
                p: 3,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    bgcolor: "#F5EEF7",
                    display: "grid",
                    placeItems: "center",
                    color: "#7B408C",
                  }}
                >
                  <HiOutlineBriefcase style={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                  Total investor inquiries
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "28px",
                  mt: 2,
                  color: "#2E1834",
                }}
              >
                {loading && !hasData ? "—" : totalItems.toLocaleString()}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "16px",
                p: 2.5,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "15px", mb: 0.5 }}>
                Investor inquiries
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#666" }}>
                Review submissions from potential investors. Select a row to
                view full details.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <div>
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  p: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  mt: 6,
                }}
              >
                <Box sx={{ mb: 2, maxWidth: 480 }}>
                  <CustomSearch
                    placeholder="Search by name, email, organization…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8f8f7" }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Submitted
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Full name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Organization
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: "12px" }}>
                          Investment range
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, fontSize: "12px" }}
                          align="right"
                        >
                          Details
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!loading && displayedItems.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{ py: 6, color: "#888" }}
                          >
                            No investor inquiries found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayedItems.map((item) => (
                          <TableRow
                            key={item._id}
                            hover
                            onClick={() => openDetails(item)}
                            sx={{
                              cursor: "pointer",
                              "&:last-child td": { border: 0 },
                            }}
                          >
                            <TableCell sx={{ fontSize: "13px" }}>
                              {formatDateShort(item.createdAt)}
                            </TableCell>
                            <TableCell
                              sx={{ fontSize: "13px", fontWeight: 600 }}
                            >
                              {item.fullName ?? "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px", color: "#555" }}>
                              {item.email ?? "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              {item.organization ?? "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: "13px" }}>
                              <Box
                                component="span"
                                sx={{
                                  px: 1.25,
                                  py: 0.35,
                                  borderRadius: "50px",
                                  bgcolor: "#F5EEF7",
                                  color: "#7B408C",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                }}
                              >
                                {item.investmentRange ?? "—"}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                endIcon={<FiChevronRight />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetails(item);
                                }}
                                sx={{
                                  color: "#7B408C",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  textTransform: "none",
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ fontSize: "13px", color: "#424242" }}>
                    {totalItems === 0
                      ? "0 results"
                      : `${start}–${end} of ${totalItems} inquiries`}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#424242",
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      Rows per page
                    </Typography>
                    <Select
                      size="small"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      sx={{ minWidth: 72, fontSize: "13px" }}
                    >
                      {ROWS_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                    <Pagination
                      count={totalPages}
                      page={pageNum}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                      disabled={loading}
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              </Box>
            </div>
          </Grid>
        </Grid>
      </Box>

      <InquiryDetailsDrawer
        open={drawerOpen}
        inquiry={selectedInquiry}
        onClose={closeDetails}
      />
    </>
  );
};

export default InvestorInquiries;

function formatDateShort(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
